/**
 * Data layer for `manual-approval-affiliate-workflow`. Thin typed fetch wrappers over /api/v1/manual-approval-affiliate-workflow.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ManualApprovalAffiliateWorkflowRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListManualApprovalAffiliateWorkflowResponse {
	count: number;
	items: ManualApprovalAffiliateWorkflowRow[];
}

export interface CreateManualApprovalAffiliateWorkflowBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/manual-approval-affiliate-workflow";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListManualApprovalAffiliateWorkflowResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListManualApprovalAffiliateWorkflowResponse>(res);
}

export async function create(body: CreateManualApprovalAffiliateWorkflowBody): Promise<ManualApprovalAffiliateWorkflowRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ManualApprovalAffiliateWorkflowRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const manualApprovalAffiliateWorkflowQueryKeys = {
	all: ["manual-approval-affiliate-workflow"] as const,
	list: () => [...manualApprovalAffiliateWorkflowQueryKeys.all] as const,
	detail: (id: string) => [...manualApprovalAffiliateWorkflowQueryKeys.all, id] as const,
};
